import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useRafCoalescedState } from "../useRafCoalescedState";
import {
  CURSOR_DRAG_RESIZE_HORIZONTAL,
  CURSOR_DRAG_RESIZE_VERTICAL,
  DRAG_RESIZE_1D_BODY_CLASS,
} from "./useDragResize1D.constants";
import type {
  UseDragResize1DParams,
  UseDragResize1DReturn,
} from "./useDragResize1D.types";
import { clampDragResizeSize, resolveDragResizeSize } from "./useDragResize1D.utils";

export type {
  ResolveDragResizeSizeInput,
  ResolveDragResizeSizeResult,
} from "./useDragResize1D.utils";
export { clampDragResizeSize, resolveDragResizeSize } from "./useDragResize1D.utils";

function readAxisSize(el: HTMLElement, axis: "horizontal" | "vertical"): number {
  const r = el.getBoundingClientRect();
  return axis === "horizontal" ? r.width : r.height;
}

function applyDimension(
  el: HTMLElement,
  axis: "horizontal" | "vertical",
  valuePx: number
) {
  if (axis === "horizontal") {
    el.style.width = `${valuePx}px`;
  } else {
    el.style.height = `${valuePx}px`;
  }
}

/**
 * One-axis pixel resize (mouse only): `mousedown` on handle → `mousemove` / `mouseup` on `window` (capture).
 * Assumes `box-sizing: border-box` on the target. LTR physical coordinates; see `invert` for right/bottom-anchored UIs.
 * If the target contains iframes, consumers may set `pointer-events: none` on them while `isResizing` is true.
 *
 * Returns stable callback refs — attach them to target/handle. Initial size applies synchronously on target attach.
 */
export function useDragResize1D<T extends HTMLElement = HTMLElement>(
  params: UseDragResize1DParams
): UseDragResize1DReturn<T> {
  const {
    axis,
    invert = false,
    min,
    max,
    initialSize,
    onSizeChange,
    onResizeEnd,
    disabled = false,
  } = params;

  const resolved = resolveDragResizeSize({ min, max, initialSize });
  const minPx = resolved.min;
  const maxPx = resolved.max;

  const [size, setSizeState] = useState(() => resolved.initialSize ?? 0);
  const [isResizing, setIsResizing] = useState(false);

  const targetElRef = useRef<HTMLElement | null>(null);
  const handleElRef = useRef<HTMLElement | null>(null);
  const isResizingRef = useRef(false);
  const lastGoodSizeRef = useRef(resolved.initialSize ?? 0);

  const paramsRef = useRef({
    axis,
    invert,
    min,
    max,
    initialSize,
    disabled,
  });
  paramsRef.current = { axis, invert, min, max, initialSize, disabled };

  const onSizeChangeRef = useRef(onSizeChange);
  const onResizeEndRef = useRef(onResizeEnd);
  onSizeChangeRef.current = onSizeChange;
  onResizeEndRef.current = onResizeEnd;

  const prevInitialSizePropRef = useRef(initialSize);
  const appliedInitialRef = useRef<{ el: HTMLElement; size: number } | null>(null);

  const dragStateRef = useRef<{
    startPointer: number;
    startSize: number;
  } | null>(null);

  const { scheduleUpdate, flush } = useRafCoalescedState(setSizeState);

  const cleanupRef = useRef<{
    removeMouseMove?: () => void;
    removeMouseUp?: () => void;
  }>({});

  const lastRegisteredRef = useRef<{
    handler: HTMLElement | null;
    listener: ((e: MouseEvent) => void) | null;
  }>({ handler: null, listener: null });

  const constraintsRef = useRef({ min: minPx, max: maxPx });
  constraintsRef.current = { min: minPx, max: maxPx };

  const performCleanup = useCallback(() => {
    cleanupRef.current.removeMouseMove?.();
    cleanupRef.current.removeMouseUp?.();
    cleanupRef.current.removeMouseMove = undefined;
    cleanupRef.current.removeMouseUp = undefined;

    flush();

    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    document.body.classList.remove(DRAG_RESIZE_1D_BODY_CLASS);

    dragStateRef.current = null;
  }, [flush]);

  const performCleanupRef = useRef(performCleanup);
  performCleanupRef.current = performCleanup;

  const endActiveDrag = useCallback(() => {
    if (!dragStateRef.current) return;
    const { min: lo, max: hi } = constraintsRef.current;
    const finalSize = clampDragResizeSize(lastGoodSizeRef.current, lo, hi, lo);
    performCleanupRef.current();
    isResizingRef.current = false;
    setIsResizing(false);
    onResizeEndRef.current?.(finalSize);
  }, []);

  const endActiveDragRef = useRef(endActiveDrag);
  endActiveDragRef.current = endActiveDrag;

  const detachHandleListener = useCallback(() => {
    const { handler, listener } = lastRegisteredRef.current;
    if (handler && listener) {
      handler.removeEventListener("mousedown", listener);
    }
    lastRegisteredRef.current = { handler: null, listener: null };
  }, []);

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      const drag = dragStateRef.current;
      if (!drag) return;

      const target = targetElRef.current;
      if (!target) {
        endActiveDragRef.current();
        return;
      }

      const { axis: ax, invert: inv } = paramsRef.current;
      const coord = ax === "vertical" ? e.clientY : e.clientX;
      const delta = coord - drag.startPointer;
      const signedDelta = inv ? -delta : delta;
      const nextRaw = drag.startSize + signedDelta;
      const { min: lo, max: hi } = constraintsRef.current;
      const next = clampDragResizeSize(nextRaw, lo, hi, lastGoodSizeRef.current);

      lastGoodSizeRef.current = next;
      applyDimension(target, ax, next);
      onSizeChangeRef.current?.(next);
      scheduleUpdate(next);
    },
    [scheduleUpdate]
  );

  const onMouseUp = useCallback((_e: MouseEvent) => {
    if (!dragStateRef.current) return;
    endActiveDragRef.current();
  }, []);

  const onMouseDown = useCallback(
    (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (paramsRef.current.disabled) return;

      const handle = handleElRef.current;
      const target = targetElRef.current;
      if (!handle || !target) return;

      e.preventDefault();

      const { axis: ax, initialSize: init } = paramsRef.current;
      const startSize = readAxisSize(target, ax);
      const startPointer = ax === "vertical" ? e.clientY : e.clientX;

      const { min: lo, max: hi } = constraintsRef.current;
      lastGoodSizeRef.current = clampDragResizeSize(startSize, lo, hi, init ?? lo);

      dragStateRef.current = {
        startPointer,
        startSize,
      };

      document.body.style.userSelect = "none";
      document.body.style.cursor =
        ax === "vertical"
          ? CURSOR_DRAG_RESIZE_VERTICAL
          : CURSOR_DRAG_RESIZE_HORIZONTAL;
      document.body.classList.add(DRAG_RESIZE_1D_BODY_CLASS);

      isResizingRef.current = true;
      setIsResizing(true);

      window.addEventListener("mousemove", onMouseMove, { capture: true });
      window.addEventListener("mouseup", onMouseUp, { capture: true });

      cleanupRef.current.removeMouseMove = () => {
        window.removeEventListener("mousemove", onMouseMove, { capture: true });
      };
      cleanupRef.current.removeMouseUp = () => {
        window.removeEventListener("mouseup", onMouseUp, { capture: true });
      };
    },
    [onMouseMove, onMouseUp]
  );

  const onMouseDownRef = useRef(onMouseDown);
  onMouseDownRef.current = onMouseDown;

  /** Stable listener identity for add/removeEventListener. */
  const stableMouseDown = useCallback((e: MouseEvent) => {
    onMouseDownRef.current(e);
  }, []);

  const attachHandleListener = useCallback(
    (handle: HTMLElement) => {
      detachHandleListener();
      handle.addEventListener("mousedown", stableMouseDown);
      lastRegisteredRef.current = {
        handler: handle,
        listener: stableMouseDown,
      };
    },
    [detachHandleListener, stableMouseDown]
  );

  const applyInitialToTarget = useCallback(
    (el: HTMLElement, sizePx: number | undefined) => {
      if (sizePx === undefined || isResizingRef.current) return;
      const { axis: ax, min: lo, max: hi } = paramsRef.current;
      const { initialSize: clamped } = resolveDragResizeSize({
        min: lo,
        max: hi,
        initialSize: sizePx,
      });
      if (clamped === undefined) return;
      appliedInitialRef.current = { el, size: clamped };
      lastGoodSizeRef.current = clamped;
      setSizeState(clamped);
      applyDimension(el, ax, clamped);
    },
    []
  );

  const targetRef = useCallback(
    (el: T | null) => {
      if (el === null) {
        appliedInitialRef.current = null;
        targetElRef.current = null;
        if (dragStateRef.current) {
          endActiveDragRef.current();
        }
        return;
      }

      targetElRef.current = el;
      applyInitialToTarget(el, paramsRef.current.initialSize);
    },
    [applyInitialToTarget]
  );

  const handleRef = useCallback(
    (el: T | null) => {
      if (el === null) {
        detachHandleListener();
        handleElRef.current = null;
        if (dragStateRef.current) {
          endActiveDragRef.current();
        }
        return;
      }

      handleElRef.current = el;
      if (!paramsRef.current.disabled) {
        attachHandleListener(el);
      }
    },
    [attachHandleListener, detachHandleListener]
  );

  /** Prop-change apply while mounted (layout phase). */
  useLayoutEffect(() => {
    if (initialSize === undefined) {
      prevInitialSizePropRef.current = undefined;
      return;
    }
    const el = targetElRef.current;
    if (!el || isResizing) return;

    const propChanged = prevInitialSizePropRef.current !== initialSize;
    prevInitialSizePropRef.current = initialSize;

    const prev = appliedInitialRef.current;
    const elChanged = !prev || prev.el !== el;
    const { initialSize: nextClamped } = resolveDragResizeSize({
      min,
      max,
      initialSize,
    });
    const clampChanged = prev != null && prev.size !== nextClamped;

    if (!propChanged && !elChanged && !clampChanged) return;

    applyInitialToTarget(el, initialSize);
  }, [initialSize, min, max, isResizing, applyInitialToTarget]);

  /**
   * `disabled` transitions while handle stays mounted:
   * - true→false: re-register mousedown
   * - true while idle: detach
   * - true mid-drag: cancel + onResizeEnd
   */
  useLayoutEffect(() => {
    if (disabled && dragStateRef.current) {
      endActiveDragRef.current();
    }

    const handle = handleElRef.current;
    if (!handle) return;

    if (disabled) {
      detachHandleListener();
    } else if (lastRegisteredRef.current.handler !== handle) {
      attachHandleListener(handle);
    }
  }, [disabled, attachHandleListener, detachHandleListener]);

  const setSize = useCallback((next: number) => {
    const { min: lo, max: hi } = constraintsRef.current;
    const safe = clampDragResizeSize(next, lo, hi, lastGoodSizeRef.current);
    lastGoodSizeRef.current = safe;
    setSizeState(safe);
    const el = targetElRef.current;
    if (el) {
      applyDimension(el, paramsRef.current.axis, safe);
    }
  }, []);

  useEffect(
    () => () => {
      detachHandleListener();

      const wasDragging = dragStateRef.current !== null;
      const { min: lo, max: hi } = constraintsRef.current;
      const finalSize = clampDragResizeSize(lastGoodSizeRef.current, lo, hi, lo);

      performCleanupRef.current();
      isResizingRef.current = false;
      setIsResizing(false);

      if (wasDragging) {
        onResizeEndRef.current?.(finalSize);
      }
    },
    [detachHandleListener]
  );

  return {
    size,
    isResizing,
    setSize,
    targetRef,
    handleRef,
  };
}
